import { combineReducers, configureStore, isRejectedWithValue, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import { Notify } from '../utility/notify';
import { authApi } from './api/auth';
import { branchApi } from './api/branch';
import { budgetApi } from './api/budget';
import { catalogApi } from './api/catalog';
import { clientApi } from './api/client';
import { companiesApi } from './api/companies';
import { companyApi } from './api/company';
import { couponApi } from './api/coupon';
import { credipagoApi } from './api/credipago';
import { creditNoteApi } from './api/creditNote';
import { discountApi } from './api/discount';
import { exchangeRateApi } from './api/exchangeRate';
import { freightApi } from './api/freight';
import { identificationTypeApi } from './api/identificationType';
import { marketApi } from './api/market';
import { marketplaceApi } from './api/marketplace';
import { materialApi } from './api/material';
import { pointOfSaleApi } from './api/pointOfSale';
import { advancePaymentApi } from './api/advancePayment';
import { productCatalogApi } from './api/productCatalog';
import { promotionApi } from './api/promotion';
import { receiptApi } from './api/receipt';
import { recordApi } from './api/record';
import { reportsApi } from './api/reports';
import { returnApi } from './api/return';
import { saleApi } from './api/sale';
import { storeApi } from './api/store';
import { ubigeoApi } from './api/ubigeo';
import { userApi } from './api/user';
import { zoneApi } from './api/zone';
import { organizationApi } from './api/organization';
import { bankApi } from './api/bank';
import AuthReducer, { setInitialState, setIsTokenExpired } from './features/auth';
import LoadingReducer from './features/loading';
import SaleReducer from './features/sale';
import UserReducer from './features/user';
import storage from './storage';
import { dashboardApi } from './api/dashboard';
import { inventoryApi } from './api/inventory';
import StoreProductReducer from './features/storeProduct';
import DashboardFilterReducer from './features/dashboardFilters';
import { despatchAdviceApi } from './api/despatchAdvice';
import { cashRegisterClosureApi } from './api/cashRegisterClosure';
import { taxExemptionReasonTypeApi } from './api/taxExemptionReasonType';
import { suggestedPriceZoneApi } from './api/suggestedPriceZones';
import { suggestedPricesApi } from './api/suggestedPrices';
import { financialAgreementApi } from './api/financialAgreement';
import { restrictedBrandApi } from './api/restrictedBrand';
import { salesOfficeApi } from './api/salesOffice';
import { getIdlePromptState, getLogoutNotice } from '../utility/auth/sessionSync';
import { environment } from '../environments/environment';
import {
  isAuthRedirectInProgress,
  isPublicAuthRoute,
  redirectToLoginAfterUnauthorized,
} from '../utility/auth/authRedirect';
import {
  captureReduxApiError,
  type RtkQueryRejectedAction,
} from '../utility/monitoring/reduxApiSentry';

interface RejectedPayload {
  status: number | string;
  originalStatus?: number;
  data?: {
    message?: string;
    errors?: Record<string, string | string[]>;
  };
}

interface RejectedAction {
  payload: RejectedPayload;
  meta?: {
    arg?: {
      endpointName?: string;
      originalArgs?: unknown;
    };
  };
}

const DEFAULT_ERROR_MESSAGE = 'Error al procesar la información';

const AUTH_ENDPOINTS_WITHOUT_UNAUTHORIZED_REDIRECT = new Set([
  'loginUser',
  'verifyTwoFactor',
  'refreshToken',
  'logoutUser',
]);

const getHttpStatus = (payload: RejectedPayload): number | string => {
  if (typeof payload.status === 'number') {
    return payload.status;
  }

  return payload.originalStatus ?? payload.status;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getCouponName = (action: RejectedAction) => {
  const originalArgs = action.meta?.arg?.originalArgs;

  if (!isRecord(originalArgs)) return undefined;

  const name = originalArgs.name;

  return typeof name === 'string' && name.trim() ? name.trim() : undefined;
};

const getApiErrorMessage = (action: RejectedAction) => {
  const message = action.payload.data?.message?.trim();

  if (message === 'CouponNameExists') {
    const couponName = getCouponName(action);

    return couponName
      ? `Ya existe un cupón con el Nombre: ${couponName}`
      : 'Ya existe un cupón con el nombre ingresado';
  }

  return message || DEFAULT_ERROR_MESSAGE;
};

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth'],
};

const rootReducer = combineReducers({
  user: UserReducer,
  auth: AuthReducer,
  loading: LoadingReducer,
  sale: SaleReducer,
  storeProduct: StoreProductReducer,
  dashboardFilter: DashboardFilterReducer,
  [userApi.reducerPath]: userApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [saleApi.reducerPath]: saleApi.reducer,
  [identificationTypeApi.reducerPath]: identificationTypeApi.reducer,
  [ubigeoApi.reducerPath]: ubigeoApi.reducer,
  [clientApi.reducerPath]: clientApi.reducer,
  [materialApi.reducerPath]: materialApi.reducer,
  [freightApi.reducerPath]: freightApi.reducer,
  [receiptApi.reducerPath]: receiptApi.reducer,
  [exchangeRateApi.reducerPath]: exchangeRateApi.reducer,
  [returnApi.reducerPath]: returnApi.reducer,
  [marketplaceApi.reducerPath]: marketplaceApi.reducer,
  [credipagoApi.reducerPath]: credipagoApi.reducer,
  [budgetApi.reducerPath]: budgetApi.reducer,
  [advancePaymentApi.reducerPath]: advancePaymentApi.reducer,
  [branchApi.reducerPath]: branchApi.reducer,
  [companyApi.reducerPath]: companyApi.reducer,
  [companiesApi.reducerPath]: companiesApi.reducer,
  [pointOfSaleApi.reducerPath]: pointOfSaleApi.reducer,
  [recordApi.reducerPath]: recordApi.reducer,
  [storeApi.reducerPath]: storeApi.reducer,
  [marketApi.reducerPath]: marketApi.reducer,
  [couponApi.reducerPath]: couponApi.reducer,
  [catalogApi.reducerPath]: catalogApi.reducer,
  [creditNoteApi.reducerPath]: creditNoteApi.reducer,
  [reportsApi.reducerPath]: reportsApi.reducer,
  [promotionApi.reducerPath]: promotionApi.reducer,
  [productCatalogApi.reducerPath]: productCatalogApi.reducer,
  [zoneApi.reducerPath]: zoneApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [bankApi.reducerPath]: bankApi.reducer,
  [discountApi.reducerPath]: discountApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [inventoryApi.reducerPath]: inventoryApi.reducer,
  [despatchAdviceApi.reducerPath]: despatchAdviceApi.reducer,
  [cashRegisterClosureApi.reducerPath]: cashRegisterClosureApi.reducer,
  [taxExemptionReasonTypeApi.reducerPath]: taxExemptionReasonTypeApi.reducer,
  [suggestedPriceZoneApi.reducerPath]: suggestedPriceZoneApi.reducer,
  [suggestedPricesApi.reducerPath]: suggestedPricesApi.reducer,
  [financialAgreementApi.reducerPath]: financialAgreementApi.reducer,
  [restrictedBrandApi.reducerPath]: restrictedBrandApi.reducer,
  [salesOfficeApi.reducerPath]: salesOfficeApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const logger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const rejectedAction = action as RejectedAction;
    const { payload } = rejectedAction;
    const { data } = payload;
    const status = getHttpStatus(payload);

    if (status === 401) {
      const endpointName = rejectedAction.meta?.arg?.endpointName;

      if (endpointName && AUTH_ENDPOINTS_WITHOUT_UNAUTHORIZED_REDIRECT.has(endpointName)) {
        return next(action);
      }

      if (isPublicAuthRoute() || isAuthRedirectInProgress()) {
        return next(action);
      }

      store.dispatch(setInitialState());
      store.dispatch(setIsTokenExpired({ expired: true }));
      store.dispatch(authApi.util.resetApiState());
      redirectToLoginAfterUnauthorized('api_unauthorized');
      return next(action);
    }

    const sessionIsClosing = !!getIdlePromptState() || !!getLogoutNotice();

    if (sessionIsClosing) {
      return next(action);
    }

    captureReduxApiError(action as RtkQueryRejectedAction);

    if (status === 500) {
      Notify.error(getApiErrorMessage(rejectedAction));
      return next(action);
    }
    if (status === 502) {
      Notify.error('Servicio no disponible');
      return next(action);
    }
    if (status === 400 && (data?.message || data?.errors)) {
      // Skip validation errors - let components handle them
      if (data.message?.includes('Command Validation Errors') || data.errors) {
        return next(action);
      }
      Notify.error(getApiErrorMessage(rejectedAction));
      return next(action);
    }
    if (status === 403) {
      window.location.assign('/access-denied');
      return next(action);
    }
  }

  return next(action);
};

export const store = configureStore({
  reducer: persistedReducer,
  devTools: !environment.app.isProduction,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      logger,
      userApi.middleware,
      authApi.middleware,
      saleApi.middleware,
      identificationTypeApi.middleware,
      ubigeoApi.middleware,
      clientApi.middleware,
      materialApi.middleware,
      freightApi.middleware,
      receiptApi.middleware,
      exchangeRateApi.middleware,
      returnApi.middleware,
      marketplaceApi.middleware,
      credipagoApi.middleware,
      budgetApi.middleware,
      advancePaymentApi.middleware,
      branchApi.middleware,
      companyApi.middleware,
      companiesApi.middleware,
      pointOfSaleApi.middleware,
      recordApi.middleware,
      storeApi.middleware,
      marketApi.middleware,
      catalogApi.middleware,
      couponApi.middleware,
      creditNoteApi.middleware,
      reportsApi.middleware,
      promotionApi.middleware,
      productCatalogApi.middleware,
      zoneApi.middleware,
      organizationApi.middleware,
      bankApi.middleware,
      discountApi.middleware,
      dashboardApi.middleware,
      inventoryApi.middleware,
      despatchAdviceApi.middleware,
      cashRegisterClosureApi.middleware,
      taxExemptionReasonTypeApi.middleware,
      suggestedPriceZoneApi.middleware,
      suggestedPricesApi.middleware,
      financialAgreementApi.middleware,
      restrictedBrandApi.middleware,
      salesOfficeApi.middleware,
    ]),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
